/** 3D viewer for a combined footing (2 columns sharing a single rectangular slab). */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525, '1/2': 1.27, '5/8': 1.5875, '3/4': 1.905, '1': 2.54, '1 3/8': 3.4925,
};

interface Props {
  L: number; B: number; Hf: number; df: number;
  bc1: number; hc1: number; x1: number;
  bc2: number; hc2: number; x2: number;
  cover: number;
  diam_bot_long: string; n_bot_long: number;
  diam_top_long: string; n_top_long: number;
  diam_trans: string; s_trans: number;
}

export function Combined3DView({
  L, B, Hf, df,
  bc1, hc1, x1, bc2, hc2, x2,
  cover,
  diam_bot_long, n_bot_long,
  diam_top_long, n_top_long,
  diam_trans, s_trans,
}: Props) {
  const maxDim = Math.max(L, B, df + 80);
  const scale = 5 / Math.max(maxDim, 1);

  const L_s = L * scale;
  const B_s = B * scale;
  const Hf_s = Hf * scale;
  const df_s = df * scale;

  const d_bot = DIAM_INCH_TO_CM[diam_bot_long] ?? 1.5875;
  const d_top = DIAM_INCH_TO_CM[diam_top_long] ?? 1.5875;
  const d_trans = DIAM_INCH_TO_CM[diam_trans] ?? 1.5875;

  // Origin = footing center in plan, bottom of slab at y=0.
  // Convert column x positions to centered scene coords.
  const x1c = x1 - L / 2;
  const x2c = x2 - L / 2;

  const yScene = (h_cm: number) => -Hf_s / 2 + h_cm * scale;

  // Bottom longitudinal bars (run along x, distributed across z)
  const botLongZs = useMemo(() => {
    if (n_bot_long <= 1) return [0];
    const usable = B - 2 * (cover + d_bot / 2);
    const step = usable / (n_bot_long - 1);
    return Array.from({ length: n_bot_long }, (_, i) => -B / 2 + cover + d_bot / 2 + i * step);
  }, [n_bot_long, B, cover, d_bot]);

  // Top longitudinal bars
  const topLongZs = useMemo(() => {
    if (n_top_long <= 1) return [0];
    const usable = B - 2 * (cover + d_top / 2);
    const step = usable / (n_top_long - 1);
    return Array.from({ length: n_top_long }, (_, i) => -B / 2 + cover + d_top / 2 + i * step);
  }, [n_top_long, B, cover, d_top]);

  // Transverse bars (along z, distributed along x)
  const transXs = useMemo(() => {
    const n = Math.max(2, Math.floor(L / s_trans));
    const start = -L / 2 + (L - (n - 1) * s_trans) / 2;
    return Array.from({ length: n }, (_, i) => start + i * s_trans);
  }, [L, s_trans]);

  const yBot = cover + d_bot / 2;
  const yTrans = cover + d_bot + d_trans / 2;
  const yTop = Hf - cover - d_top / 2;

  return (
    <Canvas
      camera={{ position: [6, 4.5, 7], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]} gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid args={[30, 30]} cellSize={0.25} cellThickness={0.4}
        cellColor="#475569" sectionSize={1} sectionThickness={1}
        sectionColor="#334155" fadeDistance={20} fadeStrength={1.2}
        position={[0, yScene(df) - 0.001, 0]} infiniteGrid />

      <Bounds fit clip observe margin={1.4}>
        {/* SOIL */}
        <mesh position={[0, yScene(df / 2), 0]}>
          <boxGeometry args={[L_s * 1.4, df_s, B_s * 1.6]} />
          <meshStandardMaterial color="#7C2D12" transparent opacity={0.10} roughness={1.0} />
        </mesh>

        {/* SLAB */}
        <mesh position={[0, yScene(Hf / 2), 0]}>
          <boxGeometry args={[L_s, Hf_s, B_s]} />
          <meshStandardMaterial color="#94A3B8" transparent opacity={0.42} roughness={0.85} />
        </mesh>
        <lineSegments position={[0, yScene(Hf / 2), 0]}>
          <edgesGeometry args={[new THREE.BoxGeometry(L_s, Hf_s, B_s)]} />
          <lineBasicMaterial color="#94A3B8" />
        </lineSegments>

        {/* COLUMNS */}
        {[
          { x: x1c, bc: bc1, hc: hc1 },
          { x: x2c, bc: bc2, hc: hc2 },
        ].map((c, i) => {
          const ch = (df + 60) - Hf;
          return (
            <group key={`col-${i}`}>
              <mesh position={[c.x * scale, yScene(Hf + ch / 2), 0]}>
                <boxGeometry args={[c.bc * scale, ch * scale, c.hc * scale]} />
                <meshStandardMaterial color="#94A3B8" transparent opacity={0.55} roughness={0.85} />
              </mesh>
              <lineSegments position={[c.x * scale, yScene(Hf + ch / 2), 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(c.bc * scale, ch * scale, c.hc * scale)]} />
                <lineBasicMaterial color="#475569" />
              </lineSegments>
            </group>
          );
        })}

        {/* BOTTOM longitudinal bars (along x) */}
        {botLongZs.map((z, i) => (
          <Cylinder key={`bl-${i}`}
            radius={(d_bot / 2) * scale} length={L_s}
            position={[0, yScene(yBot), z * scale]}
            rotation={[0, 0, Math.PI / 2]}
            color="#F59E0B"
          />
        ))}
        {/* TOP longitudinal bars (along x) */}
        {topLongZs.map((z, i) => (
          <Cylinder key={`tl-${i}`}
            radius={(d_top / 2) * scale} length={L_s}
            position={[0, yScene(yTop), z * scale]}
            rotation={[0, 0, Math.PI / 2]}
            color="#0891B2"
          />
        ))}
        {/* TRANSVERSE bars (along z, stacked along x) */}
        {transXs.map((x, i) => (
          <Cylinder key={`tr-${i}`}
            radius={(d_trans / 2) * scale} length={B_s}
            position={[x * scale, yScene(yTrans), 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color="#8B5CF6"
          />
        ))}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

function Cylinder({ radius, length, position, rotation, color }: {
  radius: number; length: number;
  position: [number, number, number]; rotation: [number, number, number];
  color: string;
}) {
  const geom = useMemo(() => new THREE.CylinderGeometry(radius, radius, length, 14), [radius, length]);
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
